/**
 * App.tsx — top-level screen state machine for EffectiveSLP.
 *
 * Routing is hand-rolled (no react-router-dom). The state space
 * is small (~14 v1.0 screens) and tied to a few booleans (session,
 * onboarding-seen, tier). A router dependency would be pure overhead.
 *
 * State machine:
 *
 *   bootstrapping
 *     ↓ (useSession resolves)
 *   no session
 *     ↓ (session appears)
 *   onboarding
 *     ↓ (carousel finishes)
 *   caseload
 *     (← student detail / form / goal form / session form)
 *
 * Conventions:
 *  - All screen files live in src/screens/.
 *  - Each screen exports a named function component.
 *  - Pure helpers (no Supabase) live in src/lib/ — those are the
 *    unit-testable surface.
 */
import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { CaseloadScreen } from './screens/CaseloadScreen';
import { StudentDetailScreen } from './screens/StudentDetailScreen';
import { StudentFormScreen } from './screens/StudentFormScreen';
import { GoalFormScreen } from './screens/GoalFormScreen';
import { SessionFormScreen } from './screens/SessionFormScreen';
import { SessionPlannerScreen } from './screens/SessionPlannerScreen';
import { SessionNotesScreen } from './screens/SessionNotesScreen';
import { OnboardingCarousel } from './screens/OnboardingCarousel';
import { AuthScreen } from './screens/AuthScreen';
import { AuthCallbackScreen } from './screens/AuthCallbackScreen';
import { HandoffViewer } from './screens/HandoffViewer';
import { HandoffDemoScreen } from './screens/HandoffDemoScreen';
import { ChildPracticePlayer } from './screens/student/ChildPracticePlayer';
import { PaywallScreen } from './screens/PaywallScreen';
import { SettingsScreen } from './screens/SettingsScreen';
import { DistrictAdminScreen } from './screens/DistrictAdminScreen';
import { AdminShell } from './screens/admin/AdminShell';
import { AdminOverviewScreen } from './screens/admin/AdminOverviewScreen';
import { InviteCodesScreen } from './screens/admin/InviteCodesScreen';
import { MemberRosterScreen } from './screens/admin/MemberRosterScreen';
import { AuditLogScreen } from './screens/admin/AuditLogScreen';
import { OrgSettingsScreen } from './screens/admin/OrgSettingsScreen';
import { AuthProvider } from './lib/auth/AuthContext';
import { useAuth } from './lib/auth/useAuth';
import { ParentAuthProvider } from './lib/auth/ParentAuthContext';
import { useParentAuth } from './lib/auth/useParentAuth';
import { usePublicPath } from './lib/routing/usePublicPath';
import { hasSeenOnboarding } from './lib/auth/onboardingStore';
import { supabase } from './lib/supabase/client';
import { ParentSignIn } from './screens/parent/ParentSignIn';
import { ParentConsent } from './screens/parent/ParentConsent';
import { ParentDashboard } from './screens/parent/ParentDashboard';
import { ParentGoalsView } from './screens/parent/ParentGoalsView';
import { ParentThreadView } from './screens/parent/ParentThreadView';
import type { AdminSubScreen } from './screens/admin/AdminShell';
import { pickParentRedirect } from './lib/parent/parentRedirect';
import { BootSkeleton } from './components/Skeleton';
import { BrandMonogram } from './lib/effectiveslp/BrandMonogram';
import { DesktopShell } from './components/DesktopShell';
import { PersistentContextBar } from './components/PersistentContextBar';
import type { BreadcrumbSegment } from './components/PersistentContextBar';
import './index.css';

type Screen =
  | { kind: 'caseload' }
  | { kind: 'auth' }
  | { kind: 'auth-callback' }
  | { kind: 'handoff-demo' }
  | { kind: 'handoff'; tokenId: string }
  | { kind: 'practice'; tokenId: string }
  | { kind: 'student'; studentId: string }
  | { kind: 'student-edit'; studentId: string }
  | { kind: 'student-new' }
  | { kind: 'goal-new'; studentId: string }
  | { kind: 'goal-edit'; studentId: string; goalId: string }
  | { kind: 'session-new'; studentId: string }
  | { kind: 'session-plan'; sessionId: string }
  | { kind: 'session-notes'; sessionId: string }
  | { kind: 'paywall' }
  | { kind: 'settings' }
  | { kind: 'district-admin' }
  | { kind: 'district-admin-overview' }
  | { kind: 'district-admin-invites' }
  | { kind: 'district-admin-members' }
  | { kind: 'district-admin-audit' }
  | { kind: 'district-admin-settings' }
  | { kind: 'parent-sign-in' }
  | { kind: 'parent-consent' }
  | { kind: 'parent-dashboard' }
  | { kind: 'parent-goals' }
  | { kind: 'parent-thread'; threadId: string };

function routeToScreen(pathname: string): Screen {
  if (pathname === '/caseload' || pathname === '') return { kind: 'caseload' };
  if (pathname === '/') return { kind: 'caseload' };
  if (pathname === '/auth') return { kind: 'auth' };
  if (pathname === '/auth/callback') return { kind: 'auth-callback' };
  if (pathname === '/handoff/demo') return { kind: 'handoff-demo' };
  const handoff = pathname.match(/^\/handoff\/([A-Za-z0-9_-]+)$/);
  if (handoff) return { kind: 'handoff', tokenId: handoff[1] ?? '' };
  const practice = pathname.match(/^\/handoff\/([A-Za-z0-9_-]+)\/practice$/);
  if (practice) return { kind: 'practice', tokenId: practice[1] ?? '' };
  if (pathname === '/upgrade') return { kind: 'paywall' };
  if (pathname === '/settings') return { kind: 'settings' };
  if (pathname === '/admin/district') return { kind: 'district-admin-overview' };
  if (pathname === '/admin/district/invites') return { kind: 'district-admin-invites' };
  if (pathname === '/admin/district/members') return { kind: 'district-admin-members' };
  if (pathname === '/admin/district/audit') return { kind: 'district-admin-audit' };
  if (pathname === '/admin/district/settings') return { kind: 'district-admin-settings' };
  if (pathname === '/parent/sign-in') return { kind: 'parent-sign-in' };
  if (pathname === '/parent/consent') return { kind: 'parent-consent' };
  if (pathname === '/parent/dashboard') return { kind: 'parent-dashboard' };
  if (pathname === '/parent/goals') return { kind: 'parent-goals' };
  const parentThread = pathname.match(/^\/parent\/thread\/([^/]+)$/);
  if (parentThread) return { kind: 'parent-thread', threadId: parentThread[1] ?? '' };
  const studentNew = pathname.match(/^\/student\/new$/);
  if (studentNew) return { kind: 'student-new' };
  const studentEdit = pathname.match(/^\/student\/([^/]+)\/edit$/);
  if (studentEdit) return { kind: 'student-edit', studentId: studentEdit[1] ?? '' };
  const goalNew = pathname.match(/^\/student\/([^/]+)\/goal\/new$/);
  if (goalNew) return { kind: 'goal-new', studentId: goalNew[1] ?? '' };
  const goalEdit = pathname.match(/^\/student\/([^/]+)\/goal\/([^/]+)$/);
  if (goalEdit) return {
    kind: 'goal-edit',
    studentId: goalEdit[1] ?? '',
    goalId: goalEdit[2] ?? '',
  };
  const sessionNew = pathname.match(/^\/student\/([^/]+)\/session\/new$/);
  if (sessionNew) return { kind: 'session-new', studentId: sessionNew[1] ?? '' };
  const sessionNotes = pathname.match(/^\/session\/([^/]+)\/notes$/);
  if (sessionNotes) return { kind: 'session-notes', sessionId: sessionNotes[1] ?? '' };
  const sessionPlan = pathname.match(/^\/session\/([^/]+)$/);
  if (sessionPlan) return { kind: 'session-plan', sessionId: sessionPlan[1] ?? '' };
  const studentDetail = pathname.match(/^\/student\/([^/]+)$/);
  if (studentDetail) return { kind: 'student', studentId: studentDetail[1] ?? '' };
  return { kind: 'caseload' };
}

/**
 * Build the breadcrumb / save-state bar for the current authenticated
 * screen. Returns null for the caseload root so the bar disappears there
 * (issue #433, acceptance criterion #8).
 */
function renderContextBar(
  screen: Screen,
  navigateTo: (path: string) => void,
): ReactElement | null {
  const caseload: BreadcrumbSegment = { label: 'Caseload', href: '/caseload' };

  switch (screen.kind) {
    case 'caseload':
      return null;
    case 'settings':
      return (
        <PersistentContextBar
          breadcrumb={[caseload, { label: 'Settings' }]}
          navigateTo={navigateTo}
          save="synced"
        />
      );
    case 'paywall':
      return (
        <PersistentContextBar
          breadcrumb={[caseload, { label: 'Upgrade' }]}
          navigateTo={navigateTo}
          save="synced"
        />
      );
    case 'student-new':
      return (
        <PersistentContextBar
          breadcrumb={[caseload, { label: 'New student' }]}
          navigateTo={navigateTo}
          save="synced"
        />
      );
    case 'student':
    case 'student-edit':
      return (
        <PersistentContextBar
          breadcrumb={[caseload, { label: 'Student' }]}
          navigateTo={navigateTo}
          save="synced"
        />
      );
    case 'goal-new':
      return (
        <PersistentContextBar
          breadcrumb={[caseload, { label: 'Student' }, { label: 'New goal' }]}
          navigateTo={navigateTo}
          save="synced"
        />
      );
    case 'goal-edit':
      return (
        <PersistentContextBar
          breadcrumb={[caseload, { label: 'Student' }, { label: 'Goal' }]}
          navigateTo={navigateTo}
          save="synced"
        />
      );
    case 'session-new':
      return (
        <PersistentContextBar
          breadcrumb={[caseload, { label: 'Student' }, { label: 'New session' }]}
          navigateTo={navigateTo}
          save="synced"
        />
      );
    case 'session-plan':
      return (
        <PersistentContextBar
          breadcrumb={[
            caseload,
            { label: 'Student' },
            { label: 'Session plan' },
          ]}
          navigateTo={navigateTo}
          save="synced"
        />
      );
    case 'session-notes':
      return (
        <PersistentContextBar
          breadcrumb={[caseload, { label: 'Student' }, { label: 'Notes' }]}
          navigateTo={navigateTo}
          save="synced"
        />
      );
    case 'district-admin':
    case 'district-admin-overview':
    case 'district-admin-invites':
    case 'district-admin-members':
    case 'district-admin-audit':
    case 'district-admin-settings':
      return (
        <PersistentContextBar
          breadcrumb={[{ label: 'District admin' }]}
          navigateTo={navigateTo}
          save="synced"
        />
      );
    default:
      return null;
  }
}

/**
 * AdminSubRoute — small wrapper that loads the admin's organization
 * id (looking up the first admin/billing_admin membership), then
 * renders the AdminShell wrapping the requested sub-screen.
 */
function AdminSubRoute({
  navigate,
  children,
}: {
  navigate: (path: string) => void;
  children: (props: { orgId: string }) => ReactElement;
}): ReactElement {
  const [orgId, setOrgId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data: user } = await supabase.auth.getUser();
      if (!user?.user) {
        setError('not_signed_in');
        return;
      }
      const { data, error: lookupErr } = await supabase
        .from('organization_memberships')
        .select('organization_id')
        .eq('user_id', user.user.id)
        .in('role', ['admin', 'billing_admin'])
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      if (lookupErr || !data) {
        setError(lookupErr?.message ?? 'not_admin');
        return;
      }
      setOrgId((data as { organization_id: string }).organization_id);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (error === 'not_signed_in') {
    return (
      <main className="screen admin">
        <p className="tagline">Sign in to access the district console.</p>
      </main>
    );
  }
  if (error || !orgId) {
    return (
      <main className="screen admin">
        <p className="error">
          You don&rsquo;t have district-admin access on EffectiveSLP.
        </p>
        <button
          type="button"
          className="link-button"
          onClick={() => navigate('/caseload')}
        >
          ← Back to caseload
        </button>
      </main>
    );
  }

  function handleAdminNav(sub: AdminSubScreen): void {
    switch (sub.kind) {
      case 'overview':
        navigate('/admin/district');
        return;
      case 'invites':
        navigate('/admin/district/invites');
        return;
      case 'members':
        navigate('/admin/district/members');
        return;
      case 'audit':
        navigate('/admin/district/audit');
        return;
      case 'settings':
        navigate('/admin/district/settings');
        return;
    }
  }

  return (
    <AdminShell onNavigate={handleAdminNav}>{children({ orgId })}</AdminShell>
  );
}

function AppShell(): ReactElement {
  const { session, loading: sessionLoading } = useAuth();
  const { navigate } = usePublicPath();
  const [screen, setScreen] = useState<Screen>(() =>
    routeToScreen(window.location.pathname),
  );
  const [seenOnboarding, setSeenOnboarding] = useState<boolean>(() =>
    hasSeenOnboarding(),
  );

  // Keep screen-state in sync with the URL bar (back/forward buttons).
  // Note: we listen to popstate directly rather than going through
  // usePublicPath's classified value, because classifyPath collapses
  // "/student/abc" to "/student/:id" — losing the actual id we need.
  useEffect(() => {
    function onPop(): void {
      setScreen(routeToScreen(window.location.pathname));
    }
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
    };
  }, []);

  // Wrap navigate so screen state stays in sync when a screen pushes
  // a new path (e.g. tapping a student row).
  function navigateTo(path: string): void {
    navigate(path);
    setScreen(routeToScreen(path));
  }

  // The parent surface lives in its own auth context (parent has no
  // SLP-side profile). Dispatch on the screen kind first so the
  // parent never crosses the SLP's auth gate.
  if (
    screen.kind === 'parent-sign-in'
    || screen.kind === 'parent-consent'
    || screen.kind === 'parent-dashboard'
    || screen.kind === 'parent-goals'
    || screen.kind === 'parent-thread'
  ) {
    return <ParentShell screen={screen} navigateTo={navigateTo} />;
  }

  // Bootstrap gate. While the Supabase session is resolving we show
  // a minimal loading shell so we don't flash onboarding to a
  // signed-in user.
  if (sessionLoading) {
    return (
      <BootSkeleton
        monogram={<BrandMonogram size={64} />}
      />
    );
  }

  // Public unauthenticated routes — these render before the session
  // check because the parent (or the unauthenticated token
  // exchange) doesn't have a session.
  // /handoff/demo is a marketing/demo surface (issue #256): it
  // renders with no Supabase call, no session, and no auth gate so
  // a prospect's IT reviewer can verify the experience. It must
  // come before the bootstrap gate below — the demo has no
  // session to wait for.
  if (screen.kind === 'handoff-demo') {
    return <HandoffDemoScreen />;
  }

  if (screen.kind === 'handoff') {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL ?? '';
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY ?? '';
    return (
      <HandoffViewer
        tokenId={screen.tokenId}
        supabaseUrl={supabaseUrl}
        supabaseAnonKey={supabaseAnonKey}
      />
    );
  }

  if (screen.kind === 'practice') {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL ?? '';
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY ?? '';
    return (
      <ChildPracticePlayer
        tokenId={screen.tokenId}
        supabaseUrl={supabaseUrl}
        supabaseAnonKey={supabaseAnonKey}
      />
    );
  }

  // Not signed in → auth screen.
  if (!session) {
    if (screen.kind === 'auth-callback') {
      // The magic-link confirmation always lands here. We don't
      // gate this on session because the tokens haven't been
      // exchanged yet at the time the screen mounts.
      return (
        <AuthCallbackScreen
          onSuccess={() => navigateTo('/caseload')}
          onError={() => navigateTo('/auth')}
        />
      );
    }
    if (!seenOnboarding) {
      return (
        <OnboardingCarousel
          onDone={() => {
            setSeenOnboarding(true);
            navigateTo('/auth');
          }}
        />
      );
    }
    return (
      <AuthScreen onSignedIn={() => navigateTo('/caseload')} />
    );
  }

  // Signed in. If we somehow land on /auth/* while signed in,
  // route to the caseload.
  if (screen.kind === 'auth' || screen.kind === 'auth-callback') {
    navigateTo('/caseload');
    return (
      <main className="screen boot">
        <p className="tagline">Redirecting…</p>
      </main>
    );
  }

  // Signed in. Route based on URL.
  let screenEl: ReactElement;
  switch (screen.kind) {
    case 'caseload':
      screenEl = (
        <CaseloadScreen
          onAddStudent={() => navigateTo('/student/new')}
          onOpenStudent={(id) => navigateTo(`/student/${id}`)}
          onOpenPaywall={() => navigateTo('/upgrade')}
          onOpenSettings={() => navigateTo('/settings')}
          onSignOut={() => navigateTo('/auth')}
        />
      );
      break;
    case 'paywall':
      screenEl = (
        <PaywallScreen
          onBack={() => navigateTo('/caseload')}
          onPurchased={() => navigateTo('/caseload')}
        />
      );
      break;
    case 'settings':
      screenEl = (
        <SettingsScreen
          onBack={() => navigateTo('/caseload')}
          onOpenPaywall={() => navigateTo('/upgrade')}
        />
      );
      break;
    case 'district-admin':
      screenEl = (
        <DistrictAdminScreen
          onBack={() => navigateTo('/caseload')}
        />
      );
      break;
    case 'district-admin-overview':
      screenEl = (
        <AdminSubRoute navigate={navigate}>
          {({ orgId }) => <AdminOverviewScreen orgId={orgId} />}
        </AdminSubRoute>
      );
      break;
    case 'district-admin-invites':
      screenEl = (
        <AdminSubRoute navigate={navigate}>
          {({ orgId }) => <InviteCodesScreen orgId={orgId} />}
        </AdminSubRoute>
      );
      break;
    case 'district-admin-members':
      screenEl = (
        <AdminSubRoute navigate={navigate}>
          {({ orgId }) => <MemberRosterScreen orgId={orgId} />}
        </AdminSubRoute>
      );
      break;
    case 'district-admin-audit':
      screenEl = (
        <AdminSubRoute navigate={navigate}>
          {({ orgId }) => <AuditLogScreen orgId={orgId} />}
        </AdminSubRoute>
      );
      break;
    case 'district-admin-settings':
      screenEl = (
        <AdminSubRoute navigate={navigate}>
          {({ orgId }) => <OrgSettingsScreen orgId={orgId} />}
        </AdminSubRoute>
      );
      break;
    case 'student-new':
      screenEl = (
        <StudentFormScreen
          mode="new"
          onSaved={(id) => navigateTo(`/student/${id}`)}
          onCancel={() => navigateTo('/caseload')}
        />
      );
      break;
    case 'student-edit':
      screenEl = (
        <StudentFormScreen
          mode="edit"
          studentId={screen.studentId}
          onSaved={(id) => navigateTo(`/student/${id}`)}
          onCancel={() => navigateTo(`/student/${screen.studentId}`)}
        />
      );
      break;
    case 'student':
      screenEl = (
        <StudentDetailScreen
          studentId={screen.studentId}
          onBack={() => window.history.back()}
          onEdit={() => navigateTo(`/student/${screen.studentId}/edit`)}
          onAddGoal={() =>
            navigateTo(`/student/${screen.studentId}/goal/new`)
          }
          onEditGoal={(goalId) =>
            navigateTo(`/student/${screen.studentId}/goal/${goalId}`)
          }
          onScheduleSession={() =>
            navigateTo(`/student/${screen.studentId}/session/new`)
          }
          onOpenSession={(sessionId) =>
            navigateTo(`/session/${sessionId}`)
          }
        />
      );
      break;
    case 'goal-new':
      screenEl = (
        <GoalFormScreen
          mode="new"
          studentId={screen.studentId}
          onSaved={() => navigateTo(`/student/${screen.studentId}`)}
          onCancel={() => navigateTo(`/student/${screen.studentId}`)}
        />
      );
      break;
    case 'goal-edit':
      screenEl = (
        <GoalFormScreen
          mode="edit"
          studentId={screen.studentId}
          goalId={screen.goalId}
          onSaved={() => navigateTo(`/student/${screen.studentId}`)}
          onCancel={() => navigateTo(`/student/${screen.studentId}`)}
        />
      );
      break;
    case 'session-new':
      screenEl = (
        <SessionFormScreen
          studentId={screen.studentId}
          onSaved={() => navigateTo(`/student/${screen.studentId}`)}
          onCancel={() => navigateTo(`/student/${screen.studentId}`)}
        />
      );
      break;
    case 'session-plan':
      screenEl = (
        <SessionPlannerScreen
          sessionId={screen.sessionId}
          onBack={() => window.history.back()}
          onOpenNotes={(id) => navigateTo(`/session/${id}/notes`)}
          onNavigateUpgrade={() => navigateTo('/upgrade')}
        />
      );
      break;
    case 'session-notes':
      screenEl = (
        <SessionNotesScreen
          sessionId={screen.sessionId}
          onBack={() => window.history.back()}
        />
      );
      break;
  }

  // Wrap the authenticated screen in the persistent shell so the
  // sidebar + context bar survive across navigation (issue #433).
  // The shell is a no-op on mobile/tablet viewports, so mobile
  // UX is unchanged. Pass navigateTo through so sidebar +
  // breadcrumb clicks reuse the same pushState path the rest of
  // the app uses — no full document reload (issue #472).
  const contextBar = renderContextBar(screen, navigateTo);
  return (
    <DesktopShell
      pathname={window.location.pathname}
      navigateTo={navigateTo}
      contextBar={contextBar}
    >
      {screenEl}
    </DesktopShell>
  );
}

/**
 * ParentShell — separate state machine for the parent-facing surface.
 *
 * The parent uses its own ParentAuthProvider (mirrors the SLP's
 * AuthProvider) but the same underlying supabase-js client. The
 * auth gate is:
 *   - No session → /parent/sign-in
 *   - Session, no parent_accounts row → /parent/consent
 *   - Session, parent_accounts row, consent_acknowledged_at null →
 *     /parent/consent
 *   - Otherwise, render the requested screen.
 *
 * Note: the parent account row is created lazily by the dashboard
 * (api-create-parent-account) so the consent screen and dashboard
 * share a single bootstrap path that always results in a row.
 */
function ParentShell({
  screen,
  navigateTo,
}: {
  screen: Extract<Screen, { kind: 'parent-sign-in' | 'parent-consent' | 'parent-dashboard' | 'parent-goals' | 'parent-thread' }>;
  navigateTo: (path: string) => void;
}): ReactElement {
  return (
    <ParentAuthProvider>
      <ParentShellInner screen={screen} navigateTo={navigateTo} />
    </ParentAuthProvider>
  );
}

export function ParentShellInner({
  screen,
  navigateTo,
}: {
  screen: Extract<Screen, { kind: 'parent-sign-in' | 'parent-consent' | 'parent-dashboard' | 'parent-goals' | 'parent-thread' }>;
  navigateTo: (path: string) => void;
}): ReactElement {
  const { session, loading, signOut } = useParentAuth();
  const [hasAccount, setHasAccount] = useState<boolean | null>(null);
  const [consentOk, setConsentOk] = useState<boolean | null>(null);

  // Compute the redirect target from the current auth/consent
  // snapshot. The actual navigateTo call lives in the effect
  // below — calling setState on a sibling component (AppShell)
  // during render surfaces a React dev-mode warning. See
  // web/src/lib/parent/parentRedirect.ts for the contract.
  // While auth is still resolving we suppress the redirect so the
  // parent doesn't flash the sign-in boot screen before the session
  // probe finishes.
  const redirectTarget = loading
    ? null
    : pickParentRedirect({
        hasSession: session !== null,
        consentOk,
        screenKind: screen.kind,
      });

  useEffect(() => {
    if (redirectTarget !== null) {
      navigateTo(redirectTarget);
    }
  }, [redirectTarget, navigateTo]);

  // While the parent has no session, only /parent/sign-in is reachable.
  // While resolving, show the full-screen skeleton so the parent never
  // sees a blank navy canvas (issue #429).
  if (loading) {
    return <BootSkeleton monogram={<BrandMonogram size={64} />} />;
  }

  // Boot flash for the sign-in bounce. The effect above fires
  // navigateTo('/parent/sign-in') after commit; we render the
  // redirect notice on this same render so the user sees a
  // flash before the destination mounts.
  if (redirectTarget === '/parent/sign-in') {
    return (
      <main className="screen boot">
        <p className="tagline">Redirecting to sign-in…</p>
      </main>
    );
  }

  // Sign-in is reachable without a session.
  if (!session) {
    if (screen.kind === 'parent-sign-in') {
      return <ParentSignIn />;
    }
    // Defensive fallback — pickParentRedirect already covers this,
    // but keep the path explicit so a future refactor that drops
    // the effect can't silently route an unauthenticated parent
    // to a parent-only screen.
    return (
      <main className="screen boot">
        <p className="tagline">Redirecting to sign-in…</p>
      </main>
    );
  }

  // Session present. Probe for the parent_accounts row + consent.
  // We only probe when we don't yet know; the screens themselves
  // keep state fresh after first load.
  const needProbe = hasAccount === null || consentOk === null;
  if (needProbe && screen.kind !== 'parent-consent') {
    return <ParentDashboardProbe
      onResult={(accountOk, consent) => {
        setHasAccount(accountOk);
        setConsentOk(consent);
      }}
      onResolve={() => {
        if (screen.kind === 'parent-dashboard') {
          // The dashboard itself will re-fetch.
          setHasAccount(true);
          setConsentOk(true);
        }
      }}
    />;
  }

  // Boot flashes for the consent-required and already-signed-in
  // bounces. Mirror the pre-fix wording so the user-visible copy
  // is unchanged.
  if (redirectTarget === '/parent/consent') {
    return (
      <main className="screen boot">
        <p className="tagline">Redirecting…</p>
      </main>
    );
  }

  if (redirectTarget === '/parent/dashboard') {
    return (
      <main className="screen boot">
        <p className="tagline">Redirecting…</p>
      </main>
    );
  }

  if (screen.kind === 'parent-consent') {
    return (
      <ParentConsent
        onAcknowledged={() => {
          setConsentOk(true);
          navigateTo('/parent/dashboard');
        }}
        onSignOut={async () => {
          await signOut();
          navigateTo('/parent/sign-in');
        }}
      />
    );
  }

  if (screen.kind === 'parent-dashboard') {
    return (
      <ParentDashboard
        navigate={navigateTo}
        onOpenConsent={() => navigateTo('/parent/consent')}
        onOpenThread={(id) => navigateTo(`/parent/thread/${id}`)}
        onOpenGoalsView={() => navigateTo('/parent/goals')}
      />
    );
  }

  if (screen.kind === 'parent-goals') {
    return (
      <ParentGoalsView
        practiceSetId={null}
        onBack={() => navigateTo('/parent/dashboard')}
      />
    );
  }

  if (screen.kind === 'parent-thread') {
    return (
      <ParentThreadView
        threadId={screen.threadId}
        onBack={() => navigateTo('/parent/dashboard')}
      />
    );
  }

  // Exhaustive — TS narrows the union to the variants above.
  return (
    <main className="screen boot">
      <p className="tagline">Redirecting…</p>
    </main>
  );
}

/**
 * ParentDashboardProbe — short-lived component that runs the
 * api-create-parent-account call to ensure a parent_accounts row
 * exists, then decides whether to send the user to /parent/consent
 * or let them through to the dashboard.
 */
function ParentDashboardProbe({
  onResult,
  onResolve,
}: {
  onResult: (accountOk: boolean, consentOk: boolean) => void;
  onResolve: () => void;
}): ReactElement {
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data, error } = await supabase.functions.invoke<{
        parent_account: {
          user_id: string;
          email: string | null;
          display_name: string | null;
          created_at: string;
          consent_acknowledged_at: string | null;
        };
      }>('api-create-parent-account', { body: {} });
      if (cancelled) return;
      if (error || !data?.parent_account) {
        onResult(false, false);
        return;
      }
      const consent = data.parent_account.consent_acknowledged_at !== null;
      onResult(true, consent);
      onResolve();
    })();
    return () => {
      cancelled = true;
    };
  }, [onResult, onResolve]);
  return (
    <BootSkeleton monogram={<BrandMonogram size={64} />} />
  );
}

export default function App(): ReactElement {
  return (
    <AuthProvider>
      <AppShell />
    </AuthProvider>
  );
}
